import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAcademicRenewalFormTexts } from '@/components/Academic/hooks/useAcademicRenewalFormTexts';
import FeedbackSurveyLayout from './FeedbackSurveyLayout';

const FEEDBACK_PLACEHOLDER_STEPS = 7;

const FeedbackSurveyPlaceholder: React.FC = () => {
  const navigate = useNavigate();
  const t = useAcademicRenewalFormTexts();
  const [step, setStep] = useState(0);
  const [applicationTypes, setApplicationTypes] = useState<string[]>([]);

  const toggleType = (value: string) => {
    setApplicationTypes(prev => (prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]));
  };

  const optionRows = t.usageFrequencyOptions;
  const isLast = step === FEEDBACK_PLACEHOLDER_STEPS - 1;

  const handlePrimary = () => {
    if (isLast) {
      navigate('/');
      return;
    }
    setStep(s => s + 1);
  };

  const handleBack = () => {
    if (step > 0) setStep(s => s - 1);
  };

  return (
    <FeedbackSurveyLayout
      title="DISGENET Feedback"
      subtitle="Help us improve by sharing your experience"
      currentIndex={step}
      totalSteps={FEEDBACK_PLACEHOLDER_STEPS}
      onBack={handleBack}
      onPrimary={handlePrimary}
      backDisabled={step === 0}
      primaryLabel={isLast ? 'Done' : 'Next'}
    >
      {step === 0 && (
        <div>
          <p className="text-base font-medium text-slate-900">
            What type of application did you primarily use DISGENET for?{' '}
            <span className="text-red-500">*</span>
          </p>
          <p className="mt-1 text-sm text-slate-500">{t.helpSelectAll}</p>
          <ul className="mt-6 space-y-3">
            {optionRows.map(opt => (
              <li key={opt.value}>
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 transition-colors hover:border-teal-300 hover:bg-teal-50/40">
                  <input
                    type="checkbox"
                    checked={applicationTypes.includes(opt.value)}
                    onChange={() => toggleType(opt.value)}
                    className="mt-0.5 h-4 w-4 shrink-0 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
                  />
                  <span className="text-sm leading-snug text-slate-700">{opt.label}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}

      {step > 0 && !isLast && (
        <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-8 text-center">
          <p className="text-sm font-medium text-slate-700">More questions coming soon</p>
          <p className="mt-2 text-sm text-slate-500">
            This step is reserved for the full DISGENET feedback questionnaire (step {step + 1} of{' '}
            {FEEDBACK_PLACEHOLDER_STEPS}).
          </p>
        </div>
      )}

      {isLast && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-8 text-center">
          <p className="text-sm font-medium text-slate-800">Feedback form under construction</p>
          <p className="mt-2 text-sm text-slate-600">
            Submission will be enabled when the full questionnaire is ready. You can close this page or go
            back.
          </p>
        </div>
      )}
    </FeedbackSurveyLayout>
  );
};

export default FeedbackSurveyPlaceholder;
